import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { SeasonalityData } from "@/hooks/useSalesForecast";

interface SeasonalitySectionProps {
  seasonality?: SeasonalityData[];
}

const getSeasonalityColor = (factor: number) => {
  if (factor >= 1.2) return "hsl(var(--chart-1))";
  if (factor >= 1.0) return "hsl(var(--chart-2))";
  if (factor >= 0.8) return "hsl(var(--chart-3))";
  return "hsl(var(--chart-4))";
};

export default function SeasonalitySection({ seasonality }: SeasonalitySectionProps) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>تحليل الموسمية</CardTitle>
          <CardDescription>
            تحديد الأشهر ذات الذروة والأشهر المنخفضة في المبيعات
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={seasonality}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="month_name" />
              <YAxis />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))', 
                  border: '1px solid hsl(var(--border))' 
                }}
                formatter={(value: number) => `${value.toFixed(2)}x`}
              />
              <Bar dataKey="seasonal_factor" radius={[8, 8, 0, 0]}>
                {seasonality?.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getSeasonalityColor(entry.seasonal_factor)} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-6 grid gap-4 md:grid-cols-4">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded" style={{ backgroundColor: 'hsl(var(--chart-1))' }} />
              <span className="text-sm">ذروة (&gt; 1.2x)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded" style={{ backgroundColor: 'hsl(var(--chart-2))' }} />
              <span className="text-sm">عادي (1.0-1.2x)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded" style={{ backgroundColor: 'hsl(var(--chart-3))' }} />
              <span className="text-sm">منخفض (0.8-1.0x)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded" style={{ backgroundColor: 'hsl(var(--chart-4))' }} />
              <span className="text-sm">ضعيف (&lt; 0.8x)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        {seasonality?.filter(s => s.peak_season).map((month) => (
          <Card key={month.month} className="border-l-4 border-l-chart-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {month.month_name} - شهر ذروة
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {month.seasonal_factor.toFixed(2)}x
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                متوسط: {month.historical_average.toLocaleString('ar-SA')} ر.س
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
